import { useState, useRef, useEffect } from 'react'
import { useTranslation } from '../i18n'
import { useTheme, type ThemeMode } from '../theme'

const themeOptions: {
  value: ThemeMode
  icon: string
  labelKey: 'theme.system' | 'theme.light' | 'theme.dark'
}[] = [
  { value: 'system', icon: 'pi pi-desktop', labelKey: 'theme.system' },
  { value: 'light', icon: 'pi pi-sun', labelKey: 'theme.light' },
  { value: 'dark', icon: 'pi pi-moon', labelKey: 'theme.dark' },
]

export function ThemeSwitcher() {
  const { t } = useTranslation()
  const { mode, setMode, accentColor, setAccentColor } = useTheme()
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  return (
    <div ref={dropdownRef} style={{ position: 'relative' }}>
      <button
        data-testid="theme-switcher"
        onClick={() => setIsOpen(!isOpen)}
        className="btn btn-secondary btn-sm"
        aria-label={t('component.themeSettings')}
        style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
      >
        <i
          className={themeOptions.find((o) => o.value === mode)?.icon ?? themeOptions[0].icon}
          aria-hidden="true"
        />
        <i className="pi pi-palette" aria-hidden="true" />
      </button>

      {isOpen && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            right: 0,
            marginTop: '0.5rem',
            background: 'var(--color-surface)',
            border: '1px solid var(--color-border)',
            borderRadius: '0.5rem',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
            minWidth: '200px',
            zIndex: 50,
          }}
        >
          <div style={{ padding: '0.75rem', borderBottom: '1px solid var(--color-border)' }}>
            <div
              style={{
                fontSize: '0.75rem',
                fontWeight: 600,
                color: 'var(--color-text-secondary)',
                marginBottom: '0.5rem',
              }}
            >
              {t('label.theme')}
            </div>
            <div style={{ display: 'flex', gap: '0.25rem' }}>
              {themeOptions.map((option) => (
                <button
                  key={option.value}
                  onClick={() => setMode(option.value)}
                  className={`btn btn-sm ${mode === option.value ? 'btn-primary' : 'btn-secondary'}`}
                  aria-label={t(option.labelKey)}
                  title={t(option.labelKey)}
                  style={{ flex: 1, padding: '0.5rem' }}
                >
                  <i className={option.icon} aria-hidden="true" />
                </button>
              ))}
            </div>
          </div>

          <div style={{ padding: '0.75rem' }}>
            <div
              style={{
                fontSize: '0.75rem',
                fontWeight: 600,
                color: 'var(--color-text-secondary)',
                marginBottom: '0.5rem',
              }}
            >
              {t('label.accentColor')}
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              {[
                { nameKey: 'color.blue' as const, value: '#2563eb' },
                { nameKey: 'color.purple' as const, value: '#7c3aed' },
                { nameKey: 'color.green' as const, value: '#059669' },
                { nameKey: 'color.orange' as const, value: '#ea580c' },
                { nameKey: 'color.pink' as const, value: '#db2777' },
              ].map((color) => (
                <button
                  key={color.nameKey}
                  onClick={() => setAccentColor(color.value)}
                  aria-label={t(color.nameKey)}
                  title={t(color.nameKey)}
                  style={{
                    width: '1.75rem',
                    height: '1.75rem',
                    borderRadius: '50%',
                    backgroundColor: color.value,
                    border:
                      accentColor === color.value
                        ? '3px solid var(--color-text)'
                        : '2px solid transparent',
                    cursor: 'pointer',
                    padding: 0,
                  }}
                />
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
